import { Building2, Users, FileText, AlertTriangle, CheckCircle, TrendingUp, Activity, Plus, Settings, RefreshCw, FileCode, ImageIcon, Video, ExternalLink, Flag } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { usePlatformStats, usePlatformContent } from '@/hooks'
import { PlatformAnalytics } from '@/components/analytics/PlatformAnalytics'
import { SystemHealthMonitor } from '@/components/admin/SystemHealthMonitor'
import { AuditLogViewer } from '@/components/admin/AuditLogViewer'
import { SystemConfiguration } from '@/components/admin/SystemConfiguration'
import { CARD_CLASSES, DASHBOARD_CONTAINER_CLASSES, DASHBOARD_SPACING } from '@/lib/constants/spacing'

interface SuperAdminDashboardProps {
  userName?: string
}

export function SuperAdminDashboard({ userName: _userName = 'Super Admin' }: SuperAdminDashboardProps) {
  const navigate = useNavigate()
  const { stats: platformStatsData, loading: statsLoading, error: statsError, refetch: refetchStats } = usePlatformStats()
  const { content: platformContent, loading: contentLoading, error: contentError } = usePlatformContent()

  const recentCompanies = [
    { id: 1, name: 'TechCorp Inc.', users: 24, content: 156, status: 'active', created: '2025-01-28' },
    { id: 2, name: 'Global Solutions', users: 18, content: 89, status: 'active', created: '2025-01-25' },
    { id: 3, name: 'Innovate Labs', users: 32, content: 234, status: 'pending', created: '2025-01-22' }
  ]

  const systemAlerts = [
    { id: 1, type: 'warning', message: 'Storage usage at 85% for company "TechCorp Inc"', time: '2 hours ago' },
    { id: 2, type: 'info', message: 'New company "Innovate Labs" awaiting approval', time: '5 hours ago' },
    { id: 3, type: 'success', message: 'Database backup completed successfully', time: '12 hours ago' }
  ]

  const quickActions = [
    {
      title: 'Create Company',
      description: 'Add a new company to the platform',
      icon: <Plus className="w-5 h-5" />,
      onClick: () => navigate('/admin'),
      color: 'bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground'
    },
    {
      title: 'Manage Companies',
      description: 'View and manage all companies',
      icon: <Building2 className="w-5 h-5" />,
      onClick: () => navigate('/admin'),
      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500 hover:text-white'
    },
    {
      title: 'Content Moderation',
      description: 'Review and moderate platform content',
      icon: <FileText className="w-5 h-5" />,
      onClick: () => navigate('/content'),
      color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500 hover:text-white'
    },
    {
      title: 'System Settings',
      description: 'Configure platform-wide settings',
      icon: <Settings className="w-5 h-5" />,
      onClick: () => navigate('/settings'),
      color: 'bg-gray-500/10 text-gray-600 dark:text-gray-400 hover:bg-gray-500 hover:text-white'
    }
  ]

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
      case 'info':
        return <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
      default:
        return <Activity className="w-5 h-5 text-gray-600 dark:text-gray-400" />
    }
  }

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'warning':
        return 'bg-yellow-500/10 border-yellow-500/20'
      case 'info':
        return 'bg-blue-500/10 border-blue-500/20'
      case 'success':
        return 'bg-green-500/10 border-green-500/20'
      default:
        return 'bg-gray-500/10 border-gray-500/20'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-600 dark:text-green-400'
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
      default:
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
    }
  }

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'presentation':
        return <FileCode className="w-4 h-4" />
      case 'pdf':
        return <FileText className="w-4 h-4" />
      case 'image':
        return <ImageIcon className="w-4 h-4" />
      case 'video':
        return <Video className="w-4 h-4" />
      default:
        return <FileText className="w-4 h-4" />
    }
  }

  const getContentTypeColor = (type: string) => {
    switch (type) {
      case 'presentation':
        return 'text-blue-600 dark:text-blue-400 bg-blue-500/10'
      case 'pdf':
        return 'text-red-600 dark:text-red-400 bg-red-500/10'
      case 'image':
        return 'text-green-600 dark:text-green-400 bg-green-500/10'
      case 'video':
        return 'text-purple-600 dark:text-purple-400 bg-purple-500/10'
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-500/10'
    }
  }

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'demo':
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20'
      case 'company':
        return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20'
      case 'admin':
        return 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20'
      default:
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400 border-gray-500/20'
    }
  }

  return (
    <div className={DASHBOARD_CONTAINER_CLASSES}>
      {/* Platform Statistics */}
      {statsError && (
        <div className={`${CARD_CLASSES} bg-destructive/10 border-destructive/20 p-6`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-destructive/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Error Loading Statistics</h3>
                <p className="text-sm text-muted-foreground">{statsError}</p>
              </div>
            </div>
            <button
              onClick={() => refetchStats()}
              className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md text-sm font-medium transition-colors hover:bg-destructive/90 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      )}

      {statsLoading && !statsError && (
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 ${DASHBOARD_SPACING.GRID_GAP}`}>
          {[1, 2, 3, 4].map((index) => (
            <div key={index} className={`${CARD_CLASSES} p-6 animate-pulse`}>
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-muted rounded-lg"></div>
              </div>
              <div className="mb-1">
                <div className="h-7 bg-muted rounded mb-2 w-20"></div>
                <div className="h-4 bg-muted rounded w-24"></div>
              </div>
              <div className="h-3 bg-muted rounded w-16"></div>
            </div>
          ))}
        </div>
      )}

      {!statsLoading && !statsError && platformStatsData && (
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 ${DASHBOARD_SPACING.GRID_GAP}`}>
          <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg`}>
                <Building2 className="w-5 h-5" />
              </div>
              <div className={`flex items-center ${DASHBOARD_SPACING.GAP_TIGHT} text-green-600 dark:text-green-400`}>
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
            <div className={DASHBOARD_SPACING.STAT_LABEL_GAP}>
              <p className="text-2xl font-bold text-foreground">{platformStatsData.totalCompanies}</p>
              <p className="text-sm text-muted-foreground">Total Companies</p>
            </div>
            <p className="text-xs text-muted-foreground">Platform-wide</p>
          </div>

          <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-green-500/10 text-green-600 dark:text-green-400 rounded-lg`}>
                <Users className="w-5 h-5" />
              </div>
              <div className={`flex items-center ${DASHBOARD_SPACING.GAP_TIGHT} text-green-600 dark:text-green-400`}>
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
            <div className={DASHBOARD_SPACING.STAT_LABEL_GAP}>
              <p className="text-2xl font-bold text-foreground">{platformStatsData.totalUsers}</p>
              <p className="text-sm text-muted-foreground">Total Users</p>
            </div>
            <p className="text-xs text-muted-foreground">Across all companies</p>
          </div>

          <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-lg`}>
                <FileText className="w-5 h-5" />
              </div>
              <div className={`flex items-center ${DASHBOARD_SPACING.GAP_TIGHT} text-green-600 dark:text-green-400`}>
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
            <div className={DASHBOARD_SPACING.STAT_LABEL_GAP}>
              <p className="text-2xl font-bold text-foreground">{platformStatsData.totalContent}</p>
              <p className="text-sm text-muted-foreground">Content Items</p>
            </div>
            <p className="text-xs text-muted-foreground">All types included</p>
          </div>

          <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg`}>
                <Activity className="w-5 h-5" />
              </div>
            </div>
            <div className={DASHBOARD_SPACING.STAT_LABEL_GAP}>
              <p className="text-2xl font-bold text-foreground">Healthy</p>
              <p className="text-sm text-muted-foreground">System Status</p>
            </div>
            <p className="text-xs text-muted-foreground">99.9% uptime</p>
          </div>
        </div>
      )}

      {/* System Health Monitor */}
      <SystemHealthMonitor />

      {/* Audit Log Viewer */}
      <AuditLogViewer />

      {/* System Configuration */}
      <SystemConfiguration />

      {/* Quick Actions */}
      <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
        <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP} ${DASHBOARD_SPACING.HEADER_MARGIN_BOTTOM}`}>
          <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-primary/10 rounded-lg`}>
            <Settings className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Quick Actions</h2>
            <p className="text-sm text-muted-foreground">
              Common platform management tasks
            </p>
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 ${DASHBOARD_SPACING.GRID_GAP}`}>
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`${DASHBOARD_SPACING.CARD_PADDING_COMPACT} border border-border rounded-lg transition-all hover:shadow-lg ${action.color}`}
            >
              <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP} ${DASHBOARD_SPACING.MARGIN_TINY}`}>
                {action.icon}
                <h3 className="font-semibold text-sm">{action.title}</h3>
              </div>
              <p className="text-xs opacity-90 text-left">
                {action.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* System Alerts */}
      <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
        <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP} ${DASHBOARD_SPACING.HEADER_MARGIN_BOTTOM}`}>
          <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-primary/10 rounded-lg`}>
            <Activity className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">System Alerts</h2>
            <p className="text-sm text-muted-foreground">
              Recent platform notifications and alerts
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {systemAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING_DENSE} flex items-start ${DASHBOARD_SPACING.LIST_ITEM_GAP} ${getAlertColor(alert.type)}`}
            >
              <div className="flex-shrink-0">
                {getAlertIcon(alert.type)}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{alert.message}</p>
                <p className="text-xs text-muted-foreground mt-1">{alert.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Companies */}
      <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP}`}>
            <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-primary/10 rounded-lg`}>
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Recent Companies</h2>
              <p className="text-sm text-muted-foreground">
                Latest companies to join the platform
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className={`${DASHBOARD_SPACING.BUTTON_PADDING} bg-primary text-primary-foreground rounded-md text-sm font-medium transition-colors hover:bg-primary/90 flex items-center justify-center ${DASHBOARD_SPACING.GAP_TIGHT} w-full sm:w-auto`}
          >
            <Building2 className="w-4 h-4" />
            <span>Manage All Companies</span>
          </button>
        </div>

        <div className="space-y-3">
          {recentCompanies.map((company) => (
            <div
              key={company.id}
              className={`${CARD_CLASSES} bg-muted/30 p-4`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-foreground truncate">{company.name}</h4>
                    <p className="text-xs text-muted-foreground">Created {company.created}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-md font-medium capitalize whitespace-nowrap ${getStatusColor(company.status)}`}>
                  {company.status}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{company.users} users</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{company.content} content items</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content Overview */}
      <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP}`}>
            <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-primary/10 rounded-lg`}>
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Platform Content</h2>
              <p className="text-sm text-muted-foreground">
                Content overview across all companies
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/content')}
            className={`${DASHBOARD_SPACING.BUTTON_PADDING} bg-primary text-primary-foreground rounded-md text-sm font-medium transition-colors hover:bg-primary/90 flex items-center justify-center ${DASHBOARD_SPACING.GAP_TIGHT} w-full sm:w-auto`}
          >
            <FileText className="w-4 h-4" />
            <span>Manage Content</span>
          </button>
        </div>

        {/* Content Statistics by Type */}
        {!statsLoading && !statsError && platformStatsData && (
          <div className="mb-6">
            <h3 className={`text-sm font-semibold text-foreground ${DASHBOARD_SPACING.MARGIN_SMALL}`}>Content by Type</h3>
            <div className={`grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 ${DASHBOARD_SPACING.GRID_GAP_DENSE}`}>
              <div className={`${CARD_CLASSES} bg-blue-500/10 border-blue-500/20 ${DASHBOARD_SPACING.CARD_PADDING_XDENSE}`}>
                <div className={`flex items-center ${DASHBOARD_SPACING.GAP_TIGHT} ${DASHBOARD_SPACING.MARGIN_MIN}`}>
                  <FileCode className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-xs text-muted-foreground">Presentations</span>
                </div>
                <p className="text-xl font-bold text-foreground">{platformStatsData.presentationCount}</p>
              </div>
              <div className={`${CARD_CLASSES} bg-red-500/10 border-red-500/20 ${DASHBOARD_SPACING.CARD_PADDING_XDENSE}`}>
                <div className={`flex items-center ${DASHBOARD_SPACING.GAP_TIGHT} ${DASHBOARD_SPACING.MARGIN_MIN}`}>
                  <FileText className="w-4 h-4 text-red-600 dark:text-red-400" />
                  <span className="text-xs text-muted-foreground">PDFs</span>
                </div>
                <p className="text-xl font-bold text-foreground">{platformStatsData.pdfCount}</p>
              </div>
              <div className={`${CARD_CLASSES} bg-green-500/10 border-green-500/20 ${DASHBOARD_SPACING.CARD_PADDING_XDENSE}`}>
                <div className={`flex items-center ${DASHBOARD_SPACING.GAP_TIGHT} ${DASHBOARD_SPACING.MARGIN_MIN}`}>
                  <ImageIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-xs text-muted-foreground">Images</span>
                </div>
                <p className="text-xl font-bold text-foreground">{platformStatsData.imageCount}</p>
              </div>
              <div className={`${CARD_CLASSES} bg-purple-500/10 border-purple-500/20 ${DASHBOARD_SPACING.CARD_PADDING_XDENSE}`}>
                <div className={`flex items-center ${DASHBOARD_SPACING.GAP_TIGHT} ${DASHBOARD_SPACING.MARGIN_MIN}`}>
                  <Video className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <span className="text-xs text-muted-foreground">Videos</span>
                </div>
                <p className="text-xl font-bold text-foreground">{platformStatsData.videoCount}</p>
              </div>
            </div>
          </div>
        )}

        {/* Content by Access Level Breakdown */}
        {!contentLoading && !contentError && platformContent.length > 0 && (
          <div className="mb-6">
            <h3 className={`text-sm font-semibold text-foreground ${DASHBOARD_SPACING.MARGIN_SMALL}`}>Content by Access Level</h3>
            <div className={`grid grid-cols-1 sm:grid-cols-3 ${DASHBOARD_SPACING.GRID_GAP_DENSE}`}>
              {['demo', 'company', 'admin'].map((level) => {
                const count = platformContent.filter((item) => item.accessLevel === level).length
                const percentage = platformContent.length > 0 ? Math.round((count / platformContent.length) * 100) : 0
                return (
                  <div key={level} className={`${CARD_CLASSES} bg-muted/30 ${DASHBOARD_SPACING.CARD_PADDING_XDENSE}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground capitalize">{level} Content</span>
                      <span className={`text-xs px-2 py-0.5 rounded-md font-medium capitalize border ${getAccessLevelColor(level)}`}>
                        {level}
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-foreground mb-1">{count}</p>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          level === 'demo' ? 'bg-gray-600 dark:bg-gray-400' :
                          level === 'company' ? 'bg-blue-600 dark:bg-blue-400' :
                          'bg-purple-600 dark:bg-purple-400'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{percentage}% of total</p>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Recent Platform Content */}
        <div>
          <h3 className={`text-sm font-semibold text-foreground ${DASHBOARD_SPACING.MARGIN_SMALL}`}>Recent Content Across All Companies</h3>
          {contentError && (
            <div className={`${CARD_CLASSES} bg-destructive/10 border-destructive/20 ${DASHBOARD_SPACING.CARD_PADDING_DENSE} mb-4`}>
              <p className="text-sm text-destructive">{contentError}</p>
            </div>
          )}
          {contentLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map((index) => (
                <div key={index} className={`${CARD_CLASSES} bg-muted/30 ${DASHBOARD_SPACING.CARD_PADDING_DENSE} animate-pulse`}>
                  <div className={`flex items-start ${DASHBOARD_SPACING.LIST_ITEM_GAP}`}>
                    <div className="w-12 h-12 bg-muted rounded-lg"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-muted rounded mb-2 w-3/4"></div>
                      <div className="h-3 bg-muted rounded mb-1 w-1/2"></div>
                      <div className="h-3 bg-muted rounded w-1/3"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!contentLoading && !contentError && platformContent.length === 0 && (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-foreground font-medium mb-1">No Content Found</p>
              <p className="text-sm text-muted-foreground">No content has been uploaded to the platform yet.</p>
            </div>
          )}
          {!contentLoading && !contentError && platformContent.length > 0 && (
            <div className="space-y-3">
              {platformContent.slice(0, 5).map((item) => (
                <div
                  key={item.id}
                  className={`${CARD_CLASSES} bg-muted/30 ${DASHBOARD_SPACING.CARD_PADDING_DENSE} hover:bg-muted/50 transition-colors cursor-pointer group`}
                  onClick={() => navigate(`/content/${item.id}`)}
                >
                  <div className={`flex items-start ${DASHBOARD_SPACING.LIST_ITEM_GAP}`}>
                    <div className={`${DASHBOARD_SPACING.ICON_PADDING} rounded-lg ${getContentTypeColor(item.type)}`}>
                      {getContentTypeIcon(item.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`flex items-start justify-between ${DASHBOARD_SPACING.GAP_TIGHT} ${DASHBOARD_SPACING.MARGIN_MIN}`}>
                        <h4 className="font-medium text-foreground text-sm group-hover:text-primary transition-colors line-clamp-1">
                          {item.title}
                        </h4>
                        <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{item.description}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-md font-medium capitalize ${getContentTypeColor(item.type)}`}>
                          {item.type}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-md font-medium capitalize border ${getAccessLevelColor(item.accessLevel)}`}>
                          {item.accessLevel}
                        </span>
                        {item.companyName && (
                          <span className="text-xs text-muted-foreground">
                            • {item.companyName}
                          </span>
                        )}
                        <span className="text-xs text-muted-foreground ml-auto">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content Moderation Queue (Flagged Content) */}
      {!contentLoading && !contentError && platformContent.length > 0 && (
        <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
          <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP} ${DASHBOARD_SPACING.HEADER_MARGIN_BOTTOM}`}>
            <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-orange-500/10 rounded-lg`}>
              <Flag className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Content Moderation Queue</h2>
              <p className="text-sm text-muted-foreground">
                Flagged content requiring review
              </p>
            </div>
          </div>

          <div className="text-center py-6">
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
            <p className="text-foreground font-medium mb-1">All Content Clear</p>
            <p className="text-sm text-muted-foreground">No content has been flagged for moderation at this time.</p>
          </div>
        </div>
      )}

      {/* Platform Health */}
      {!statsLoading && !statsError && platformStatsData && (
        <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
          <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP} ${DASHBOARD_SPACING.HEADER_MARGIN_BOTTOM}`}>
            <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-primary/10 rounded-lg`}>
              <Activity className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Platform Health</h2>
              <p className="text-sm text-muted-foreground">
                System performance and metrics
              </p>
            </div>
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${DASHBOARD_SPACING.GRID_GAP}`}>
            <div className={`${CARD_CLASSES} bg-muted/30 ${DASHBOARD_SPACING.CARD_PADDING_DENSE}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Server Uptime</span>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">99.9%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-green-600 dark:bg-green-400 h-2 rounded-full" style={{ width: '99.9%' }}></div>
              </div>
            </div>
            <div className={`${CARD_CLASSES} bg-muted/30 ${DASHBOARD_SPACING.CARD_PADDING_DENSE}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Content Distribution</span>
                <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
                  {platformStatsData.totalContent > 0
                    ? Math.round((platformStatsData.presentationCount / platformStatsData.totalContent) * 100)
                    : 0}% Presentations
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-purple-600 dark:bg-purple-400 h-2 rounded-full" style={{
                  width: platformStatsData.totalContent > 0
                    ? `${(platformStatsData.presentationCount / platformStatsData.totalContent) * 100}%`
                    : '0%'
                }}></div>
              </div>
            </div>
            <div className={`${CARD_CLASSES} bg-muted/30 ${DASHBOARD_SPACING.CARD_PADDING_DENSE}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Total Companies</span>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{platformStatsData.totalCompanies}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full" style={{
                  width: platformStatsData.totalCompanies > 0
                    ? `${Math.min(platformStatsData.totalCompanies * 2, 100)}%`
                    : '0%'
                }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Platform Analytics Section */}
      <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
        <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP} ${DASHBOARD_SPACING.HEADER_MARGIN_BOTTOM}`}>
          <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-primary/10 rounded-lg`}>
            <TrendingUp className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Platform Analytics</h2>
            <p className="text-sm text-muted-foreground">
              Platform-wide statistics and insights
            </p>
          </div>
        </div>
        <PlatformAnalytics />
      </div>
    </div>
  )
}
